// src/plugins/autotrack/utils.ts

import { ElementInfo } from './types';

export class AutoTrackUtils {
  /**
   * Extract comprehensive element information
   */
  static getElementInfo(
    element: Element,
    captureAttributes: string[] = [],
    maxTextLength: number = 100
  ): ElementInfo {
    const info: ElementInfo = {
      tagName: element.tagName.toLowerCase(),
      id: element.id ?? undefined,
      className: element.className ?? undefined,
      text: element.textContent?.trim().substring(0, maxTextLength) ?? undefined,
    };

    // Capture additional attributes if specified
    if (captureAttributes.length > 0) {
      info.attributes = {};
      captureAttributes.forEach(attr => {
        const value = element.getAttribute(attr);
        if (value) {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          info.attributes![attr] = value;
        }
      });
    }

    // Generate CSS selector
    info.selector = this.generateSelector(element);

    // Generate XPath
    info.xpath = this.generateXPath(element);

    return info;
  }

  /**
   * Generate a unique CSS selector for an element
   */
  static generateSelector(element: Element): string {
    if (element.id) {
      return `#${element.id}`;
    }

    const path: string[] = [];
    let current: Element | null = element;

    while (current && current.nodeType === Node.ELEMENT_NODE) {
      let selector = current.tagName.toLowerCase();

      if (current.className) {
        selector += `.${current.className.split(' ').join('.')}`;
      }

      // Add nth-child if needed for uniqueness
      if (current.parentElement) {
        const siblings = Array.from(current.parentElement.children);
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const sameTagSiblings = siblings.filter(sibling => sibling.tagName === current!.tagName);

        if (sameTagSiblings.length > 1) {
          const index = sameTagSiblings.indexOf(current) + 1;
          selector += `:nth-child(${index})`;
        }
      }

      path.unshift(selector);
      current = current.parentElement;
    }

    return path.join(' > ');
  }

  /**
   * Generate XPath for an element
   */
  static generateXPath(element: Element): string {
    if (element.id) {
      return `//*[@id="${element.id}"]`;
    }

    const path: string[] = [];
    let current: Element | null = element;

    while (current && current.nodeType === Node.ELEMENT_NODE) {
      let index = 1;
      let sibling = current.previousElementSibling;

      while (sibling) {
        if (sibling.tagName === current.tagName) {
          index++;
        }
        sibling = sibling.previousElementSibling;
      }

      const tagName = current.tagName.toLowerCase();
      path.unshift(`${tagName}[${index}]`);
      current = current.parentElement;
    }

    return `/${path.join('/')}`;
  }

  /**
   * Check if element should be excluded from tracking
   */
  static shouldExcludeElement(
    element: Element,
    excludeSelector?: string,
    includeSelector?: string
  ): boolean {
    // Check Do Not Track attribute
    if (
      element.hasAttribute('data-insights-ignore') ||
      element.hasAttribute('data-no-track') ||
      element.hasAttribute('data-autotrack-ignore')
    ) {
      return true;
    }

    // Check exclude selector
    if (excludeSelector && element.matches?.(excludeSelector)) {
      return true;
    }

    // Check include selector (if specified, element must match)
    if (includeSelector && !element.matches?.(includeSelector)) {
      return true;
    }

    return false;
  }

  /**
   * Debounce function for performance optimization
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static debounce<T extends (...args: any[]) => any>(
    func: T,
    wait: number
  ): (...args: Parameters<T>) => void {
    let timeout: NodeJS.Timeout;

    return (...args: Parameters<T>) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  }

  /**
   * Throttle function for performance optimization
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static throttle<T extends (...args: any[]) => any>(
    func: T,
    limit: number
  ): (...args: Parameters<T>) => void {
    let inThrottle: boolean;

    return (...args: Parameters<T>) => {
      if (!inThrottle) {
        func(...args);
        inThrottle = true;
        setTimeout(() => (inThrottle = false), limit);
      }
    };
  }

  /**
   * Get viewport dimensions
   */
  static getViewportDimensions() {
    return {
      width: window.innerWidth ?? document.documentElement.clientWidth,
      height: window.innerHeight ?? document.documentElement.clientHeight,
    };
  }

  /**
   * Get document dimensions
   */
  static getDocumentDimensions() {
    return {
      width: Math.max(
        document.body.scrollWidth,
        document.body.offsetWidth,
        document.documentElement.clientWidth,
        document.documentElement.scrollWidth,
        document.documentElement.offsetWidth
      ),
      height: Math.max(
        document.body.scrollHeight,
        document.body.offsetHeight,
        document.documentElement.clientHeight,
        document.documentElement.scrollHeight,
        document.documentElement.offsetHeight
      ),
    };
  }

  /**
   * Calculate scroll percentage
   */
  static getScrollPercentage(): number {
    const scrollTop = window.pageYOffset ?? document.documentElement.scrollTop;
    const documentHeight = this.getDocumentDimensions().height;
    const viewportHeight = this.getViewportDimensions().height;

    const scrollableHeight = documentHeight - viewportHeight;

    if (scrollableHeight <= 0) {
      return 100;
    }

    return Math.min(100, Math.max(0, (scrollTop / scrollableHeight) * 100));
  }

  /**
   * Check if browser supports Do Not Track
   */
  static respectsDoNotTrack(): boolean {
    return (
      navigator.doNotTrack === '1' ||
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).doNotTrack === '1' ||
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (navigator as any).msDoNotTrack === '1'
    );
  }

  /**
   * Safely get element text content
   */
  static getElementText(element: Element, maxLength: number = 100): string {
    const text =
      element.textContent ?? element.getAttribute('alt') ?? element.getAttribute('title') ?? '';
    return text.trim().substring(0, maxLength);
  }

  /**
   * Check if element is visible in viewport
   */
  static isElementVisible(element: Element): boolean {
    const rect = element.getBoundingClientRect();
    const viewport = this.getViewportDimensions();

    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= viewport.height &&
      rect.right <= viewport.width
    );
  }

  /**
   * Get element position relative to document
   */
  static getElementPosition(element: Element): { x: number; y: number } {
    const rect = element.getBoundingClientRect();
    return {
      x: rect.left + window.pageXOffset,
      y: rect.top + window.pageYOffset,
    };
  }
}
